import React, { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  IconButton,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  generateUtilityClasses,
} from '@mui/material'
import { styled } from '@mui/system'
import EditIcon from '@mui/icons-material/Edit'
import DeleteIcon from '@mui/icons-material/Delete'
import { storageGet, storageSet } from '../../../../utils/storage'
import { useDispatch } from 'react-redux'
import { visuallyHidden } from '@mui/utils'
import { getComparator, stableSort } from '../../../../utils/sorting'
import ConfirmDialog from '../../../../components/ConfirmDialog'
import { UspConfigurationPaymentFormViewResult } from '../../../../models'
import { useDeletePaymentFormMutation } from '../../../../services'
import { enqueueNotification } from '../../../../redux/slices/notificationsSlice'
import { CustomCircular } from '../../../../ui'
import { formatNumber } from '../../../../utils/general'
import { usePopover } from '../../../../components/custom-popover'
import CustomPopover from '../../../../components/custom-popover/custom-popover'
import Iconify from '../../../../components/iconify'
import Label from '../../../../components/label'

const classes = generateUtilityClasses('Grid', ['root', 'activeItem', 'loading', 'tableHead', 'tableCellName', 'inactiveItem'])

const StyledGrid = styled(Grid)(({ theme }) => ({
  [`&.${classes.root}`]: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    margin: theme.spacing(3, 3, 3, 3),
  },
  [`& .${classes.activeItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.success.main,
    borderRadius: '4px',
  },
  [`& .${classes.inactiveItem}`]: {
    padding: theme.spacing(0.5, 0, 0, 0),
    width: '84px',
    height: '22px',
    fontSize: '0.8em',
    fontWeight: 500,
    color: '#FFFFFF',
    textAlign: 'center',
    backgroundColor: theme.palette.error.light,
    borderRadius: '4px',
  },
  [`& .${classes.tableHead}`]: {
    fontWeight: 600,
    background: theme.palette?.background?.main,
  },
  [`& .${classes.tableCellName}`]: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
  },
  [`& .${classes.loading}`]: {
    verticalAlign: 'top',
  },
}))

interface Props {
  data: UspConfigurationPaymentFormViewResult[]
  loading: boolean
  setData: Dispatch<SetStateAction<UspConfigurationPaymentFormViewResult[]>>
  setOpenAddEditModal: Dispatch<SetStateAction<boolean>>
  setType: Dispatch<SetStateAction<string>>
  setRowData: Dispatch<SetStateAction<UspConfigurationPaymentFormViewResult>>
}
const PaymentPaymentsFormTable = ({ data, loading, setData, setOpenAddEditModal, setRowData, setType }: Props) => {
  const [deletePaymentForm, { isSuccess: deleteIsSuccess }] = useDeletePaymentFormMutation()
  const initialOrder = (storageGet('payments_form_order') || 'asc') as 'asc' | 'desc'
  const initialOrderBy = (storageGet('payments_form_order_by') || 'PaymentForm') as keyof UspConfigurationPaymentFormViewResult
  const [order, setOrder] = React.useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = React.useState<keyof UspConfigurationPaymentFormViewResult>(initialOrderBy)
  const [deleteConfirmDialog, setDeleteConfirmDialog] = React.useState<{ paymentFormId: string } | undefined>(undefined)
  const dispatch = useDispatch()
  const [selected, setSelected] = useState<any>(null)
  const popover = usePopover()

  const onRequestSort = (event: React.MouseEvent<unknown>, property: keyof UspConfigurationPaymentFormViewResult) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
    storageSet('payments_form_order', isAsc ? 'desc' : 'asc')
    storageSet('payments_form_order_by', property)
  }

  const createSortHandler = (property: keyof UspConfigurationPaymentFormViewResult) => (event: React.MouseEvent<unknown>) => {
    onRequestSort(event, property)
  }

  const handleDelete = async (): Promise<void> => {
    if (deleteConfirmDialog?.paymentFormId) {
      await deletePaymentForm(deleteConfirmDialog)
    }
    setDeleteConfirmDialog(undefined)
  }

  const handleOpen = () => {
    setType('edit')
    setOpenAddEditModal(true)
  }

  useEffect(() => {
    if (deleteConfirmDialog?.paymentFormId) {
      if (deleteIsSuccess) {
        let updatedData: UspConfigurationPaymentFormViewResult[]
        updatedData = data.map((item) => (item.ID?.toString() === deleteConfirmDialog.paymentFormId ? { ...item, IsActive: false } : item))
        setData(updatedData)
        dispatch(enqueueNotification({ message: 'Success' }))
      }
    }
  }, [deleteIsSuccess])

  return (
    <>
      <TableContainer component={Paper} sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 118px)' }} className={classes.root}>
        <Table sx={{ minWidth: 650 }} aria-label='simple table' stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'PaymentForm' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'PaymentForm'}
                  direction={orderBy === 'PaymentForm' ? order : 'asc'}
                  onClick={createSortHandler('PaymentForm')}
                >
                  Name
                  {orderBy === 'PaymentForm' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'ProcessingFee' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ProcessingFee'}
                  direction={orderBy === 'ProcessingFee' ? order : 'asc'}
                  onClick={createSortHandler('ProcessingFee')}
                >
                  Process Fee
                  {orderBy === 'ProcessingFee' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsCollected' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsCollected'}
                  direction={orderBy === 'IsCollected' ? order : 'asc'}
                  onClick={createSortHandler('IsCollected')}
                >
                  Collected
                  {orderBy === 'IsCollected' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsWriteOff' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsWriteOff'}
                  direction={orderBy === 'IsWriteOff' ? order : 'asc'}
                  onClick={createSortHandler('IsWriteOff')}
                >
                  Write Off
                  {orderBy === 'IsWriteOff' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsAllowChargeback' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsAllowChargeback'}
                  direction={orderBy === 'IsAllowChargeback' ? order : 'asc'}
                  onClick={createSortHandler('IsAllowChargeback')}
                >
                  Chargeback
                  {orderBy === 'IsAllowChargeback' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsIRS8300' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsIRS8300'}
                  direction={orderBy === 'IsIRS8300' ? order : 'asc'}
                  onClick={createSortHandler('IsIRS8300')}
                >
                  IRS8300
                  {orderBy === 'IsIRS8300' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>
              <TableCell className={classes.tableHead} sortDirection={orderBy === 'ChartOfAccountSales' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ChartOfAccountSales'}
                  direction={orderBy === 'ChartOfAccountSales' ? order : 'asc'}
                  onClick={createSortHandler('ChartOfAccountSales')}
                >
                  CoA (Sales)
                  {orderBy === 'ChartOfAccountSales' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} sortDirection={orderBy === 'ChartOfAccountFinance' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'ChartOfAccountFinance'}
                  direction={orderBy === 'ChartOfAccountFinance' ? order : 'asc'}
                  onClick={createSortHandler('ChartOfAccountFinance')}
                >
                  CoA (Finance)
                  {orderBy === 'ChartOfAccountFinance' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsSystem' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsSystem'}
                  direction={orderBy === 'IsSystem' ? order : 'asc'}
                  onClick={createSortHandler('IsSystem')}
                >
                  System
                  {orderBy === 'IsSystem' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} sortDirection={orderBy === 'IsActive' ? order : false}>
                <TableSortLabel
                  active={orderBy === 'IsActive'}
                  direction={orderBy === 'IsActive' ? order : 'asc'}
                  onClick={createSortHandler('IsActive')}
                >
                  Status
                  {orderBy === 'IsActive' ? (
                    <Box component='span' sx={visuallyHidden}>
                      {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                    </Box>
                  ) : null}
                </TableSortLabel>
              </TableCell>

              <TableCell className={classes.tableHead} align={'right'}>
                Action
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {stableSort(getComparator(order, orderBy), data as any)?.map((item) => (
              <TableRow key={item.ID} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                <TableCell
                  className={classes.tableCellName}
                  onClick={() => {
                    if (item?.IsSystem ? false : true) {
                      handleOpen()
                      setRowData(item as any)
                    }
                  }}
                >
                  {item.PaymentForm}
                </TableCell>
                <TableCell>{formatNumber(item?.ProcessingFee as string)}</TableCell>
                <TableCell>{item?.IsCollected ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsWriteOff ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsAllowChargeback ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.IsIRS8300 ? 'Yes' : 'No'}</TableCell>
                <TableCell>{item?.ChartOfAccountSales}</TableCell>
                <TableCell>{item?.ChartOfAccountFinance}</TableCell>
                <TableCell>{item?.IsSystem ? 'Yes' : 'No'}</TableCell>
                <TableCell>
                  <Label color={item.IsActive ? 'success' : 'error'}>{item.IsActive ? 'Active' : 'Inactive'}</Label>
                </TableCell>
                <TableCell align={'right'}>
                  <IconButton
                    onClick={(event: React.MouseEvent<HTMLElement>) => {
                      setSelected(item)
                      popover.onOpen(event)
                    }}
                  >
                    <Iconify icon='eva:more-vertical-fill' />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {data?.length === 0 && (
              <TableRow>
                <TableCell colSpan={10} align='center'>
                  No results
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <CustomCircular open={loading} />
      <ConfirmDialog
        open={!!deleteConfirmDialog}
        onOk={handleDelete}
        handleClose={() => setDeleteConfirmDialog(undefined)}
        notification={'Are you sure you wish to delete?'}
      />

      <CustomPopover open={popover.open} onClose={popover.onClose} arrow='top-right' sx={{ width: 140 }}>
        <MenuItem
          disabled={selected?.IsSystem ? true : false}
          onClick={(e) => {
            handleOpen()
            setRowData(selected as any)
            popover.onClose()
          }}
        >
          <Iconify icon='solar:pen-bold' />
          Edit
        </MenuItem>
        <MenuItem
          disabled={selected?.IsSystem || !selected?.IsActive ? true : false}
          onClick={() => {
            setDeleteConfirmDialog({ paymentFormId: selected.ID?.toString() as string })
            popover.onClose()
          }}
          sx={{ color: 'error.main' }}
        >
          <Iconify icon='solar:trash-bin-trash-bold' />
          Delete
        </MenuItem>
      </CustomPopover>
    </>
  )
}

export default PaymentPaymentsFormTable
